import ReactDOM from "react-dom/client";
import { ConfigProvider, Empty } from "antd";
import "antd/dist/reset.css";
import Answer from "./components/Answer";
import { StyleSheetManager } from "styled-components";
import trTR from "antd/locale/tr_TR";
import { IAnswerModel } from "./types";
import { INVALID_VALUE_MESSAGE } from "./constants";

class App extends HTMLElement {
  private _value: string | null = null;
  private _root: ReactDOM.Root | null = null;
  private _initialized: boolean = false;
  private _parseError: boolean = false;

  static get observedAttributes() {
    return ["value"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    if (!this._initialized) {
      this.initializeWidget();
      this._initialized = true;
    }
  }

  disconnectedCallback() {
    if (this._root) {
      this._root.unmount();
      this._root = null;
    }
  }

  private parseItem(): IAnswerModel {
    this._parseError = false;
    if (!this._value) return {};

    try {
      const parsed = JSON.parse(this._value);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        this._parseError = true;
        return {};
      }
      return parsed as IAnswerModel;
    } catch (error) {
      console.error("verigor-answer-renderer: invalid value JSON", error);
      this._parseError = true;
      return {};
    }
  }

  private _render() {
    if (!this._root) return;

    const item = this.parseItem();

    this._root.render(
      <StyleSheetManager target={this.shadowRoot!}>
        <ConfigProvider locale={trTR}>
          {this._parseError ? (
            <Empty description={INVALID_VALUE_MESSAGE} />
          ) : (
            <Answer item={item} container={this.shadowRoot!} />
          )}
        </ConfigProvider>
      </StyleSheetManager>
    );
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string) {
    if (name === "value") {
      this._value = newValue;
      this._render();
    }
  }

  get value() {
    return this._value ?? "";
  }

  set value(value: string) {
    this._value = value;
    this._render();
  }

  private async initializeWidget(): Promise<void> {
    if (!this.shadowRoot) return;

    try {
      if (this._root) {
        return;
      }

      const mountPoint = document.createElement("div");
      this.shadowRoot.appendChild(mountPoint);

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = `${window.location.origin}/assets/index.css`;
      this.shadowRoot.appendChild(link);

      const antdStylesheet = document.createElement("link");
      antdStylesheet.rel = "stylesheet";
      antdStylesheet.href =
        "https://cdnjs.cloudflare.com/ajax/libs/antd/4.23.0/antd.min.css";
      this.shadowRoot.appendChild(antdStylesheet);

      this._root = ReactDOM.createRoot(mountPoint);
      this._render();
    } catch (error) {
      console.error("Error initializing widget:", error);
    }
  }
}

export default App;
